import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import { Link, useLocation } from 'react-router-dom';

import NotifyBell from '../NotifyBell';
import NavUser from '../NavUser/NavUser';
import ParivarSelector from '../ParivarSelector/ParivarSelector';

// Official Avdhoot Vishram logo — the brand appears as the logo only,
// with no text, tagline or decorative frame beside it.
import logo from '../../assets/slider-img/logo3.webp';

const MEDIA_LINKS = [
  { to: '/gallery-category', key: '/gallery-category', label: 'Gallery' },
  { to: '/bhajan-category', key: '/bhajan-category', label: 'Bhajan' },
  { to: '/video-category', key: '/video-category', label: 'Video' },
  { to: '/publication-category', key: '/publication-category', label: 'Publication' },
];

function MediaDropdown({ activeKey, pathname }) {
  const [open, setOpen] = useState(false);
  const active = MEDIA_LINKS.some((m) => m.key === activeKey);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <div className={`nav-media ${active ? 'active' : ''} ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="nav-media-toggle"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
      >
        <span>Media</span>
        <i className="fas fa-chevron-down nav-media-chevron" aria-hidden="true"></i>
      </button>
      <div className="nav-media-menu">
        {MEDIA_LINKS.map((m) => (
          <Link key={m.key} to={m.to} className={m.key === activeKey ? 'active' : ''} onClick={() => setOpen(false)}>
            <span>{m.label}</span>
            <i className="fas fa-arrow-right nav-media-item-arrow" aria-hidden="true"></i>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Navigationbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // Helper to determine active nav for grouped routes
  const getActiveKey = () => {
    const path = location.pathname;
    if (path.startsWith('/bhajan')) return '/bhajan-category';
    if (path.startsWith('/publication')) return '/publication-category';
    if (path.startsWith('/gallery')) return '/gallery-category';
    if (path.startsWith('/video')) return '/video-category';
    if (path.startsWith('/share-knowledge')) return '/share-knowledge';
    return path;
  };

  // Home is intentionally absent — the logo is the home link (top bar on
  // mobile, center logo on desktop). Live Darshan leads the left group so it
  // reads on the left side of the centered logo.
  const links = [
    { media: true },
    { to: '/news-events', key: '/news-events', label: 'News & Events' },
    { to: '/share-knowledge', key: '/share-knowledge', label: 'Share Knowledge' },
    { to: '/live', key: '/live', label: 'Live Darshan' },
    { to: '/contact', key: '/contact', label: 'Contact Us' },
    { to: '/about', key: '/about', label: 'About Us' },
  ];

  // Split links into left/right groups (order preserved), leaving the center
  // for the logo. Left carries Media + News & Events + Share Knowledge + Live
  // Darshan; right carries Contact Us + About Us.
  const leftLinks = links.slice(0, 4);
  const rightLinks = links.slice(4);

  const handleToggle = (open) => {
    if (open) {
      window.dispatchEvent(new CustomEvent('av:close-notifications'));
    }
    setExpanded(open);
  };

  return (
    <Navbar
      collapseOnSelect
      expand="xl"
      fixed="top"
      className={`custom-navbar ${scrolled ? 'scrolled' : ''}`}
      expanded={expanded}
      onToggle={handleToggle}
    >
      <Container fluid className="nav-shell">
        <Navbar.Toggle aria-controls="responsive-navbar-nav" />
        <div className="nav-bell-slot">
          <NavUser variant="icon" />
          <NotifyBell />
        </div>

        <div className="nav-center-wrap">
          <Navbar.Brand as={Link} to="/" className="brand-wrap" aria-label="Avdhoot Vishram — Home">
            <span className="brand-logo">
              <img src={logo} alt="Avdhoot Vishram" className="brand-logo-img" />
            </span>
          </Navbar.Brand>
        </div>

        {/* react-bootstrap swaps this element between `collapse`, `collapsing`
            and `collapse show`, but `show` only ever appears once the panel is
            already open - so CSS alone cannot tell an opening panel from a
            closing one. `expanded` can, and passing it as a class lets the
            stylesheet run the right animation in the right direction. */}
        <Navbar.Collapse
          className={`nav-collapse ${expanded ? 'nav-panel-in' : 'nav-panel-out'}`}
          id="responsive-navbar-nav"
        >
          <ParivarSelector mobile />

          <Nav activeKey={getActiveKey()} className="nav-left">
            {leftLinks.map((l) =>
              l.media ? (
                <MediaDropdown key="media" activeKey={getActiveKey()} pathname={location.pathname} />
              ) : (
                <Nav.Link
                  as={Link}
                  to={l.to}
                  eventKey={l.key}
                  key={l.key}
                  className={getActiveKey() === l.key ? 'active' : ''}
                >
                  {l.label}
                </Nav.Link>
              )
            )}
          </Nav>

          <div className="nav-logo-gap">
            <Link to="/" className="nav-center-logo" aria-label="Avdhoot Vishram — Home">
              <img src={logo} alt="Avdhoot Vishram" className="nav-center-logo-img" />
            </Link>
          </div>

          <Nav activeKey={getActiveKey()} className="nav-right">
            {rightLinks.map((l) => (
              <Nav.Link
                as={Link}
                to={l.to}
                eventKey={l.key}
                key={l.key}
                className={getActiveKey() === l.key ? 'active' : ''}
              >
                {l.label}
              </Nav.Link>
            ))}
            <ParivarSelector />
            <NavUser />
            <NotifyBell />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default Navigationbar;
